import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { catchError, map, Observable, of, ReplaySubject, shareReplay, switchMap } from 'rxjs';

import { TenantService, UserService } from '@celum/authentication';
import { CelumPropertiesProvider, isTruthy } from '@celum/core';
import { Account } from '@celum/sacc/domain';

@Injectable({ providedIn: 'root' })
export class AdminService {
  private isAdmin$ = inject(UserService).currentUser$.pipe(
    map(user => user?.admin),
    isTruthy(),
    shareReplay(1)
  );

  private apiUrl = CelumPropertiesProvider.properties.authentication.saccUrl;

  private http = inject(HttpClient);
  private tenantService = inject(TenantService);
  private sanitizer = inject(DomSanitizer);

  public isAdmin = toSignal(this.isAdmin$);
  public organization = toSignal(
    this.isAdmin$.pipe(
      map(() => this.tenantService.getCurrentTenantId({ fallbackToUrl: true })),
      isTruthy(),
      switchMap(tenantId => this.getOrganizationInfo(tenantId))
    )
  );

  public getOrganizationInfo(tenantId: string): Observable<Account> {
    if (!tenantId) {
      return of(null);
    }

    return this.http.get<Account>(`${this.apiUrl}/accounts/${tenantId}`).pipe(
      switchMap(account => {
        // If the account has a logo download link, we need to fetch the logo, because img tag fails to fetch it directly
        // browser blocks the request because of different origin policy (ERR_BLOCKED_BY_ORB)
        if (account.accountLogoDownloadLink) {
          return this.http.get(account.accountLogoDownloadLink, { responseType: 'blob' }).pipe(
            switchMap(blob => {
              const subject = new ReplaySubject<string>();
              const reader = new FileReader();

              // called once readAsDataURL is completed
              reader.onload = (e: any) => {
                const safe = this.sanitizer.bypassSecurityTrustUrl(`${e.target.result}`) as string;
                subject.next(safe);
                subject.complete();
              };

              reader.readAsDataURL(blob);
              return subject;
            }),
            map((logo: string) => {
              return { ...account, accountLogoDownloadLink: logo };
            })
          );
        } else {
          return of(account);
        }
      }),
      catchError(error => {
        console.error('AdminService: error on getting organization info', error.error);

        return of(null);
      })
    );
  }
}
